import { HttpErrorResponse } from '@angular/common/http';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { Observable, ObservableInput } from 'rxjs';

import { ApiError } from './model/api-error';
import { ApiException } from './model/api-exception';
import { Association, Associations } from './model/assoication';
import { Individual } from './model/individual';
import { MembershipModel } from './model/membership.model';

export function empty(value: number | string | object): boolean;
export function empty(value: any): boolean {
  if (value === null || value === undefined) {
    return true;
  }

  switch (typeof value) {
    case 'string':
      return (value === '' || value.trim() === '');
    case 'number':
      return isNaN(value) || !value;
    case 'object':
      if (value instanceof Array) {
        return value.length === 0;
      }
      return !value;
  }
}

export function serializeQueryParam(query: any) {
  return Object.keys(query).reduce((value, key) => {
    if (value) {
      value += '&';
    } else {
      value = '?';
    }

    return value + encodeURI(key) + '=' + encodeURI(query[key]);
  }, '');
}

export const now = new Date();

export function DateValidator(earliest: Date, latest: Date): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (empty(control.value)) {
      return null;
    }

    const date = new Date(control.value);

    if (! (date instanceof Date) || isNaN(date.valueOf())) {
      return { 'date' : 'invalid date' };
    }

    if (date < earliest) {
      return { 'date' : 'early' };
    } else if (date > latest) {
      return { 'date' : 'late' };
    }

    return null;
  };
}

export function CatchError(err: HttpErrorResponse, caught: Observable<any>): ObservableInput<any> {
  if (err.error && 'error' in err.error && err.error.error === true) {
    throw new ApiError(err.error.message, err.error.code || err.status, err.status);
  }

  if (err.status === 403) {
    throw new ApiError(err.statusText, err.status, err.status);
  }

  if (err.status === 412) {
    if(err.error && err.error.Message)
      throw new ApiError(err.error.Message, 412, 412);
    else 
      throw new ApiError("Order conditions weren't met. Please try again.", 412, 412);
  }

  if (err.error && 'ExceptionType' in err.error) {
    throw new ApiException(err.error);
  }

  if (err.error) {
    throw err.error;
  }

  if (err.statusText && err.statusText !== 'OK') {
    throw new Error(err.statusText);
  }

  throw err;
}

export function CPP(mbr: MembershipModel): boolean {
  return mbr.mbt_flip_customer_member_flag
    && mbr.mbt_includes_insurance_flag_ext
    && (
      mbr.mbt_code === 'Certified'
      || mbr.mbt_code === 'Professional'
      || mbr.mbt_code === 'Hair Professional'
      || mbr.mbt_code === 'Practitioner'
    );
}

export function CPPA(mbr: MembershipModel): boolean {
  return mbr.mbt_flip_customer_member_flag
    && (
      mbr.mbt_code === 'Certified'
      || mbr.mbt_code === 'Professional'
      || mbr.mbt_code === 'Hair Professional'
      || mbr.mbt_code === 'Practitioner'
      || mbr.mbt_code === 'Associate'
    );
}

export function Student(mbr: MembershipModel): boolean {
  return mbr.mbt_code === 'Student' || mbr.mbt_code == 'Free Network';
}

export function Active(mbr: MembershipModel): boolean {
  return (mbr.mbr_terminate_date === null || mbr.mbr_terminate_date === undefined)
  && (mbr.mbr_expire_date instanceof Date && mbr.mbr_expire_date > now);
}

export const MemberType = (mbt_code: string) => (mbr: MembershipModel): boolean => {
  return mbr.mbt_code === mbt_code;
}

export const NotMemberType = (mbt_code: string) => (mbr: MembershipModel): boolean => {
  return mbr.mbt_code !== mbt_code;
}

export const MbrAsn = (asn: Association) => (mbr: MembershipModel) => mbr.mbr_asn_code === asn;

export function isLoggedIn(individual: Individual): boolean {
  return (
    typeof individual.cst_key === 'string'
    && individual.cst_key.length === 36
    && typeof individual.cst_recno === 'number'
    && individual.cst_recno > 0
  );
}

export function SortAsn(a: Association, b: Association): number {
  const x = Associations.indexOf(a);
  const y = Associations.indexOf(b);

  if (x < y) {
    return -1;
  } else if (x > y) {
    return 1;
  }

  return 0;
}

export function SortMembershipsByAsn(a: MembershipModel, b: MembershipModel): number {
  return SortAsn(a.mbr_asn_code, b.mbr_asn_code);
}
